import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import { ActivityImage } from './ActivityImage'
import { useTheme } from '@/hooks/useTheme'
import { Transaction } from '@/domain'
import { responsiveAdapter } from '@/utils'
import { FormattedTransactionItem } from '@/utils/formatTransactionItem'

interface ActivityItemProps extends Transaction, FormattedTransactionItem {
  onPress?: (transaction: Transaction) => void
  height?: number
  isOutsideSavingsFlow?: boolean
  isLastActivities?: boolean
  // Enhanced transaction data
  currencyIconUrl?: string
  currencyColorReference?: string | [string, string, ...string[]]
  currencyName?: string
  formattedAmount?: string
  formattedCreditAmount?: string
  formattedAmountPlusFee?: string
  formattedDate?: string
  formattedTime?: string
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  onPress,
  uri,
  backgroundColor,
  name,
  title,
  secondaryValue,
  isSentToNonUser,
  height,
  isOutsideSavingsFlow: _isOutsideSavingsFlow,
  isLastActivities = false,
  // Enhanced transaction data
  currencyIconUrl,
  currencyColorReference,
  currencyName,
  ...item
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Extract transaction details using RN logic
  const isPending = item.state === 'processing'
  const isFailed = ['declined', 'cancelled'].includes(item.state)
  const isRefunded = item.state === 'refunded'
  const isFailedOrRefunded = isFailed || isRefunded
  const isOutgoing = item.direction === 'send'
  const isConvert = item.type === 'swap_convert'
  const isSell = item.type === 'swap_sell'
  const isWithdrew = item.type === 'external_withdraw'

  // Format amount text like in RN version
  const formatAmountText = () => {
    let formattedAmount = item.formattedAmount || ''

    // Добавляем валюту к сумме если её нет
    const currency = item.amount?.currency || ''
    if (formattedAmount && currency && !formattedAmount.includes(currency)) {
      formattedAmount = `${formattedAmount} ${currency}`
    }

    if (isFailedOrRefunded) {
      return formattedAmount
    }

    if (isConvert) {
      formattedAmount = item.formattedCreditAmount || formattedAmount
    }

    if (isSell || isWithdrew) {
      formattedAmount = item.formattedAmountPlusFee || formattedAmount
    }

    return (!isOutgoing ? '+' : '-') + formattedAmount
  }

  // Get secondary value symbol like in RN version
  const getSecondaryValueSymbol = () => {
    if (isSell) return '+'
    if (isConvert) return '-'
    return '≈'
  }

  const handlePress = () => {
    if (onPress) {
      onPress(item)
    }
  }

  return (
    <CardItem.Root
      onPress={handlePress}
      style={{
        paddingTop: `${responsiveAdapter(12)}px`,
        paddingBottom: `${responsiveAdapter(12)}px`,
        ...(height && { height: `${height}px` }),
      }}
    >
      {/* Activity Image - like RN version */}
      <ActivityImage
        uri={item.type === 'kem_to_kem' ? uri : currencyIconUrl || uri} // For kem_to_kem use user avatar, otherwise currency icon
        backgroundColor={
          item.type === 'kem_to_kem'
            ? backgroundColor
            : currencyColorReference || backgroundColor
        }
        name={item.type === 'kem_to_kem' ? name : currencyName || name}
        isSentToNonUser={isSentToNonUser}
        iconName={item.iconName}
        iconSize={item.iconSize}
        transaction={item}
      />

      {/* Content */}
      <CardItem.Content
        style={{
          gap: `${responsiveAdapter(6)}px`,
          minWidth: 0, // Позволяет flex элементу сжиматься меньше своего содержимого
        }}
      >
        {/* Title */}
        <CardItem.Title
          title={title}
          style={{
            fontSize: `${responsiveAdapter(16)}px`,
            minWidth: 0, // Позволяет тексту обрезаться
          }}
        />

        {/* Subtitle or Status */}
        {!isFailedOrRefunded && item.subTitle && (
          <CardItem.Description
            description={item.subTitle}
            style={{ fontSize: `${responsiveAdapter(14)}px` }}
          />
        )}

        {/* Failed/Refunded status */}
        {isFailedOrRefunded && (
          <BaseView row align="center" gap={4}>
            <AppIcons
              name="triangleWarning"
              size={16}
              color="icon.disabledInputIcon"
            />
            <CardItem.Description
              description={t(
                isFailed ? 'failed' : 'transactionStates.refunded',
              )}
              style={{ fontSize: `${responsiveAdapter(14)}px` }}
            />
          </BaseView>
        )}

        {/* Date/Time */}
        {!isFailedOrRefunded && (
          <CardItem.Description
            description={
              isLastActivities
                ? item.formattedDate || ''
                : item.formattedTime || ''
            }
            style={{ fontSize: `${responsiveAdapter(14)}px` }}
          />
        )}
      </CardItem.Content>

      {/* Right side - Amount and secondary info */}
      <CardItem.Footer style={{ gap: `${responsiveAdapter(3)}px` }}>
        {/* Pending state */}
        {isPending && (
          <BaseView gap={3} align="flex-end">
            <CardItem.Description
              description={formatAmountText()}
              style={{ color: theme.colors.label.default }}
            />
            <BaseText fontSize={14} color="label.description">
              {t('translation:pending')}
            </BaseText>
          </BaseView>
        )}

        {/* Normal state */}
        {!isPending && (
          <CardItem.Description
            description={formatAmountText()}
            style={{
              color: isFailedOrRefunded
                ? theme.colors.label.disabled
                : !isOutgoing
                  ? theme.colors.label.flagProfit
                  : theme.colors.label.default,
              textDecoration: isFailedOrRefunded ? 'line-through' : 'none',
            }}
          />
        )}

        {/* Secondary value (for conversions, sells, etc.) */}
        {!isFailedOrRefunded && !isPending && secondaryValue && (
          <CardItem.Description
            description={
              // For kem_to_kem, secondaryValue already includes ≈ symbol
              item.type === 'kem_to_kem'
                ? secondaryValue
                : `${getSecondaryValueSymbol()}${secondaryValue}`
            }
            style={{
              fontSize: `${responsiveAdapter(14)}px`,
              ...(isSell && { color: theme.colors.label.flagProfit }),
            }}
          />
        )}
      </CardItem.Footer>
    </CardItem.Root>
  )
}
